import React, { useState } from 'react';
import { FiCheck, FiAward, FiShield, FiStar, FiTruck, FiClock } from 'react-icons/fi';

const BADGE_CONFIG = {
  identity_verified:  { icon: FiCheck,  label: 'Identity Verified', color: '#059669', bg: '#D1FAE5' },
  business_verified:  { icon: FiShield, label: 'Business Verified', color: '#1D4ED8', bg: '#DBEAFE' },
  tax_compliant:      { icon: FiAward,  label: 'Tax Compliant', color: '#7C3AED', bg: '#EDE9FE' },
  top_seller:         { icon: FiStar,   label: 'Top Seller', color: '#D97706', bg: '#FEF3C7' },
  fast_shipper:       { icon: FiTruck,  label: 'Fast Shipper', color: '#0891B2', bg: '#CFFAFE' },
  quality_seller:     { icon: FiStar,   label: 'Quality Seller', color: '#059669', bg: '#D1FAE5' },
  established:        { icon: FiClock,  label: 'Established Seller', color: '#64748B', bg: '#F1F5F9' },
};

const LEVEL_CONFIG = {
  none:     { label: '', color: '', shield: '' },
  basic:    { label: 'Verified', color: '#059669', shield: '🛡️' },
  business: { label: 'Business Verified', color: '#1D4ED8', shield: '🏢' },
  premium:  { label: 'Premium Seller', color: '#D97706', shield: '⭐' },
};

/**
 * VerifiedBadge - shows the main verification shield + individual badges
 *
 * Props:
 *   verification  - { status, level, badges: [] }
 *   size          - 'sm' | 'md' | 'lg'  (default 'md')
 *   showBadges    - show individual badge pills  (default false)
 *   inline        - render inline next to store name (default false)
 */
const VerifiedBadge = ({ verification, size = 'md', showBadges = false, inline = false }) => {
  const [tooltip, setTooltip] = useState(null);

  if (!verification || verification.status !== 'verified') return null;

  const level = LEVEL_CONFIG[verification.level] || LEVEL_CONFIG.none;
  if (!level.label) return null;

  const sizes = {
    sm: { shield: 14, font: '11px', gap: '3px', pad: '2px 6px', badgeFont: '10px' },
    md: { shield: 16, font: '12px', gap: '4px', pad: '3px 8px', badgeFont: '11px' },
    lg: { shield: 20, font: '14px', gap: '6px', pad: '4px 12px', badgeFont: '12px' },
  };
  const s = sizes[size] || sizes.md;

  return (
    <div style={{ display: inline ? 'inline-flex' : 'flex', flexDirection: inline ? 'row' : 'column', gap: '6px', alignItems: inline ? 'center' : 'flex-start' }}>
      {/* Main verification shield */}
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: s.gap,
          background: `${level.color}12`, border: `1px solid ${level.color}30`,
          borderRadius: '20px', padding: s.pad, fontSize: s.font, fontWeight: 700,
          color: level.color, whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: s.shield }}>{level.shield}</span>
        {level.label}
        <FiCheck size={s.shield - 2} strokeWidth={3} />
      </span>

      {/* Individual badges */}
      {showBadges && verification.badges?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {verification.badges.map(badgeKey => {
            const badge = BADGE_CONFIG[badgeKey];
            if (!badge) return null;
            const Icon = badge.icon;
            return (
              <span
                key={badgeKey}
                onMouseEnter={() => setTooltip(badgeKey)}
                onMouseLeave={() => setTooltip(null)}
                style={{
                  position: 'relative',
                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                  background: badge.bg, color: badge.color,
                  borderRadius: '12px', padding: '2px 8px',
                  fontSize: s.badgeFont, fontWeight: 600, cursor: 'default',
                }}
              >
                <Icon size={12} />
                {badge.label}
                {tooltip === badgeKey && (
                  <span style={{
                    position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
                    background: '#1F2937', color: '#FFF', padding: '4px 10px', borderRadius: '6px',
                    fontSize: '11px', whiteSpace: 'nowrap', zIndex: 100, fontWeight: 500,
                  }}>
                    {badge.label}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Mini verified checkmark — for use next to store name in tight spaces
 */
export const VerifiedCheck = ({ verification, size = 14 }) => {
  if (!verification || verification.status !== 'verified') return null;
  const level = LEVEL_CONFIG[verification.level];
  if (!level?.color) return null;

  return (
    <span title={level.label} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size + 4, height: size + 4, borderRadius: '50%',
      background: level.color, color: '#FFF', marginLeft: '4px', flexShrink: 0,
    }}>
      <FiCheck size={size - 2} strokeWidth={3} />
    </span>
  );
};

export default VerifiedBadge;
